import {
  isAddressEmpty,
  isAddressEqual,
} from "@/components/Checkout/components/CheckoutSummary/CheckoutEditShippingForm";
import ButtonContainer from "@/components/Checkout/components/CheckoutSummary/components/Buttons/ButtonContainer";
import ClearButton from "@/components/Checkout/components/CheckoutSummary/components/Buttons/ClearButton";

import { ShippingAddress } from "@/components/Checkout/types";
import { SolidButton } from "@/components/StoreComponents/StoreButton";
import { useAppSelector } from "@/redux/hooks";
import { useTrackActions } from "@/utils/hooks/useTracking";
import { getFullAddressWithNames } from "@/utils/StringUtils";
import {
  AddCircleOutlineIcon,
  AnalyticsTrackingEvent,
  TABLET_SCREEN_SIZE,
  Button,
} from "@brandclub/common-ui";
import { Box, styled } from "@mui/material";
import { useEffect, useState } from "react";
import { useCheckoutUI } from "../../../CheckoutUIProvider";
import { useCheckoutCartInfo } from "../../../hooks/useCheckoutCartInfo";

import { validateAddress } from "@/api/rest/authenticated/Address";
import AddressForm from "./AddressForm";

import { ResponsiveDialog } from "@/components/StoreComponents/StoreDialogs/ResponsiveDialog";
import StoreRadio from "@/components/StoreComponents/StoreRadio";

const Container = styled("div")(({ theme }) => ({
  marginBottom: 20,
  ".existing": {
    display: "flex",
    flexDirection: "column",
    gap: 0,

    [`@media screen and (max-width: ${TABLET_SCREEN_SIZE}px)`]: {
      gap: 0,
    },
    ".addresses_container": {
      paddingRight: "12px",
      "&::-webkit-scrollbar": {
        width: "4px",
      },
      "&::-webkit-scrollbar-track": {
        background: theme.vars.palette.grey[100],
        borderRadius: "4px",
      },
      "&::-webkit-scrollbar-thumb": {
        background: theme.vars.palette.grey[400],
        borderRadius: "4px",
      },
    },
    ".info_row": {
      cursor: "pointer",
      display: "flex",
      alignItems: "flex-start",
      fontSize: 14,
      fontWeight: 500,
      color: theme.vars.palette.text.primary,
      marginBottom: 10,
      ".icon": {
        width: 30,
        height: 30,
        objectFit: "contain",
        marginRight: 10,
        [`@media screen and (max-width: ${TABLET_SCREEN_SIZE}px)`]: {
          width: 20,
          height: 20,
          marginRight: 8,
        },
      },
      ".add_new_icon": {
        lineHeight: 0,
        marginRight: 12,
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        fontSize: 22,
        [`@media screen and (max-width: ${TABLET_SCREEN_SIZE}px)`]: {
          marginRight: 10,
          fontSize: 17,
        },
      },
    },
    ".add_new_button": {
      marginTop: 0,
      [`@media screen and (max-width: ${TABLET_SCREEN_SIZE}px)`]: {
        marginTop: 0,
      },
    },
    ".view_more_addresses": {
      cursor: "pointer",
      textDecoration: "underline",
    },
  },
  ".title": {
    fontSize: 20,
    fontWeight: 600,
    marginBottom: 10,
  },
  ".actions": {
    marginTop: 30,
    display: "flex",
    gap: 20,
    justifyContent: "space-between",
    alignItems: "center",
    width: "100%",
  },
}));

const ApproveSuggestedAddress = ({
  originalAddress,
  suggestedAddress,
  onChange,
  onDismiss,
}: {
  originalAddress?: Partial<ShippingAddress>;
  suggestedAddress: Partial<ShippingAddress>;
  onChange: (address: Partial<ShippingAddress>) => void;
  onDismiss: () => void;
}) => {
  return (
    <Box display="flex" flexDirection="column" gap={2}>
      <Box>We found a more accurate format for your address.</Box>
      <Box>
        <Box fontWeight={600}>Original</Box>
        <Box>{originalAddress?.address1}</Box>
        {originalAddress?.address2 && <Box>{originalAddress?.address2}</Box>}
        <Box>
          {originalAddress?.city}, {originalAddress?.state}{" "}
          {originalAddress?.zipCode}
        </Box>
      </Box>
      <Box>
        <Box fontWeight={600}>Suggested</Box>
        <Box>{suggestedAddress.address1}</Box>
        {suggestedAddress.address2 && <Box>{suggestedAddress.address2}</Box>}
        <Box>
          {suggestedAddress.city}, {suggestedAddress.state}{" "}
          {suggestedAddress.zipCode}
        </Box>
      </Box>
      <Box
        width="100%"
        pt="16px"
        display="flex"
        justifyContent="center"
        flexDirection="column"
        gap={"16px"}
      >
        <Button
          variant="solid"
          color="primary"
          sx={{
            width: "100%",
          }}
          disableMaxWidth
          onClick={() => {
            onChange(suggestedAddress);
          }}
        >
          Use suggested address
        </Button>
        <Button
          variant="outline"
          color="primary"
          sx={{
            width: "100%",
          }}
          disableMaxWidth
          onClick={() => {
            onDismiss();
          }}
        >
          Use original address
        </Button>
      </Box>
    </Box>
  );
};

const UseAddressAnyways = ({
  originalAddress,
  onSelectEdit,
  onSelectUseAnyways,
}: {
  originalAddress?: Partial<ShippingAddress>;
  onSelectEdit: () => void;
  onSelectUseAnyways: () => void;
}) => {
  return (
    <Box display="flex" flexDirection="column" gap={2}>
      <Box>We are unable to find a match for your address.</Box>
      <Box>
        <Box>
          {originalAddress?.firstName} {originalAddress?.lastName}
        </Box>
        <Box>{originalAddress?.address1}</Box>
        {originalAddress?.address2 && <Box>{originalAddress?.address2}</Box>}
        <Box>
          {originalAddress?.city}, {originalAddress?.state}{" "}
          {originalAddress?.zipCode}
        </Box>
      </Box>
      <Box
        width="100%"
        pt="16px"
        display="flex"
        justifyContent="center"
        flexDirection="column"
        gap={"16px"}
      >
        <Button
          variant="solid"
          color="primary"
          sx={{
            width: "100%",
          }}
          disableMaxWidth
          onClick={() => {
            onSelectEdit();
          }}
        >
          Edit address
        </Button>
        <Button
          variant="outline"
          color="primary"
          sx={{
            width: "100%",
          }}
          disableMaxWidth
          onClick={() => {
            onSelectUseAnyways();
          }}
        >
          Use this address
        </Button>
      </Box>
    </Box>
  );
};

const EditFormContainer = styled("div")(({ theme }) => ({
  padding: "0px 28px 20px 28px",
  display: "flex",
  flexDirection: "column",
  boxSizing: "border-box",
  gap: "16px",
  color: theme.vars.palette.text.primary,
  [`@media screen and (max-width: ${TABLET_SCREEN_SIZE}px)`]: {
    overflowY: "scroll",
    padding: "20px 20px 20px 20px",
  },

  ".title": {
    fontSize: 18,
    fontWeight: 600,
    marginBottom: 8,
  },
}));

const CheckoutEditShippingForm = ({
  currentAddress,
  availableAddresses = [],
  handleSaveShippingAddress,
  handleSelectShippingAddress,
  onCancel,
  disableCancel = false,
  loading = false,
}: {
  availableAddresses?: ShippingAddress[];
  currentAddress?: ShippingAddress;
  handleSaveShippingAddress: (event: {
    address: Partial<ShippingAddress>;
    skipValidation?: boolean;
  }) => void;
  handleSelectShippingAddress?: (address: Partial<ShippingAddress>) => void;
  onCancel?: () => void;
  disableCancel?: boolean;
  loading?: boolean;
}) => {
  const [validationLoading, setValidationLoading] = useState(false);
  const addresses = availableAddresses;
  const [trackAction] = useTrackActions();
  const [machineState] = useCheckoutUI();
  const { items } = useCheckoutCartInfo();
  // @ts-ignore
  const { checkoutIntent, userRetailerCheckoutOrder } = machineState.context;
  const checkoutIntentId = checkoutIntent?.checkoutIntentId;
  const orderId = userRetailerCheckoutOrder?.orderId;
  const isPostSubmission = !!orderId;

  const phoneNumberFromProfile = useAppSelector(
    (state) => state.userProfile?.phoneNumber
  );

  const save = (address: Partial<ShippingAddress>, skipValidation = false) => {
    handleSaveShippingAddress({ address, skipValidation });
    setSuggestedAddress(null);
    setFormValue({
      firstName: "",
      lastName: "",
      address1: "",
      address2: "",
      city: "",
      state: "",
      zipCode: "",
    });
    setAddNewOpen(false);
  };

  const [viewMoreAddresses, setViewMoreAddresses] = useState(false);

  const [addressValidationError, setAddressValidationError] =
    useState<string | null>();
  const [addNewOpen, setAddNewOpen] = useState(false);

  // do we need both?
  const [selectedAddressId, setSelectedAddressId] =
    useState<string | null>(null);
  const selectedAddress = addresses?.find(
    (address) => address.id === selectedAddressId
  );

  // Input state
  const [formValue, setFormValue] = useState<Partial<ShippingAddress>>({
    firstName: currentAddress?.firstName,
    lastName: currentAddress?.lastName,
    address1: currentAddress?.address1,
    address2: currentAddress?.address2,
    city: currentAddress?.city,
    state: currentAddress?.state,
    zipCode: currentAddress?.zipCode,
  });
  const [suggestedAddress, setSuggestedAddress] =
    useState<Partial<ShippingAddress> | null>(null);

  // Used to track what address we just validated to set the save button state
  const [addressInputToValidate, setAddressInputToValidate] =
    useState<Partial<ShippingAddress> | null>(null);

  useEffect(() => {
    // If the selected address is not set, find the index of the matching address

    const indexOfMatchingAddress =
      addresses?.findIndex((address) =>
        isAddressEqual(address, currentAddress)
      ) ?? -1;

    if (indexOfMatchingAddress !== -1) {
      setSelectedAddressId(addresses?.[indexOfMatchingAddress].id);
    }
  }, [addresses, currentAddress, selectedAddress]);

  const clearInput = () => {
    if (addNewOpen) {
      setFormValue({
        address1: "",
        address2: "",
        city: "",
        state: "",
        zipCode: "",
        firstName: "",
        lastName: "",
      });
    } else {
      onCancel?.();
    }
  };

  const hasSelectedExistingAddress =
    selectedAddressId !== null && selectedAddress;

  const hasFormValue =
    formValue.address1 &&
    formValue.city &&
    formValue.state &&
    formValue.zipCode &&
    formValue.firstName &&
    formValue.lastName;

  const canSubmit =
    (hasSelectedExistingAddress || hasFormValue) &&
    // and it does not match the current address
    (!isAddressEqual(selectedAddress, currentAddress) ||
      !currentAddress ||
      (!isAddressEqual(formValue as ShippingAddress, currentAddress) &&
        !isAddressEmpty(formValue as ShippingAddress)));

  const haveAlreadyValidatedInput = Boolean(
    addressInputToValidate &&
      isAddressEqual(
        formValue as ShippingAddress,
        addressInputToValidate as ShippingAddress
      )
  );

  const handleValidation = async () => {
    setValidationLoading(true);
    setAddressValidationError(null);
    const { address1, address2, city, state, zipCode, firstName, lastName } =
      formValue;

    setAddressInputToValidate(formValue);

    const validationResult = await validateAddress({
      address1: address1!,
      address2: address2!,
      city: city!,
      state: state!,
      zipCode: zipCode!,
      firstName: firstName!,
      lastName: lastName!,
      country: "US",
      countryCode: "US",
    });

    if (validationResult.status === "failed" || !validationResult.isValid) {
      setAddressValidationError(
        validationResult.errorMessage ||
          "Invalid address. Please check your address details."
      );
      setValidationLoading(false);
      return;
    }

    // Check if the validated address is different from the input
    const validatedAddress = validationResult.validationResult?.result?.address;

    if (validatedAddress) {
      const formattedAddressLines =
        validatedAddress?.postalAddress?.addressLines || [];
      const formattedCity = validatedAddress?.postalAddress?.locality;
      const formattedState =
        validatedAddress?.postalAddress?.administrativeArea;
      const formattedZip = validatedAddress?.postalAddress?.postalCode;

      const isAddressDifferent =
        (formattedAddressLines.length > 0 &&
          formattedAddressLines[0].toLowerCase() !== address1?.toLowerCase()) ||
        (formattedAddressLines.length > 1 &&
          formattedAddressLines[1].toLowerCase() !==
            (address2 || "").toLowerCase()) ||
        (formattedCity &&
          formattedCity.toLowerCase() !== city?.toLowerCase()) ||
        (formattedState &&
          formattedState.toLowerCase() !== state?.toLowerCase()) ||
        (formattedZip && formattedZip !== zipCode);

      if (isAddressDifferent) {
        setSuggestedAddress({
          address1: formattedAddressLines[0],
          address2: formattedAddressLines[1],
          city: formattedCity,
          state: formattedState,
          zipCode: formattedZip,
          firstName: firstName!,
          lastName: lastName!,
          country: "US",
        });
        return;
      }
    }

    setValidationLoading(false);

    save({
      ...formValue,
      phoneNumber: phoneNumberFromProfile as string,
    });
  };

  const getBottomDrawerContent = () => {
    if (suggestedAddress) {
      return (
        <EditFormContainer>
          <div className="title">Suggested address</div>
          <ApproveSuggestedAddress
            originalAddress={formValue}
            suggestedAddress={suggestedAddress}
            onChange={(address) => {
              // Set the form value to the suggested address and save it
              setFormValue(address);
              save(address);
              clearInput();
            }}
            onDismiss={() => {
              // Dismiss suggestion and save original address (already validated)
              save(formValue);
              clearInput();
            }}
          />
        </EditFormContainer>
      );
    }

    if (addressValidationError) {
      return (
        <EditFormContainer>
          <div className="title">Oops!</div>
          <UseAddressAnyways
            originalAddress={formValue}
            onSelectEdit={() => {
              setAddressValidationError(null);
            }}
            onSelectUseAnyways={() => {
              setAddressValidationError(null);
              save(formValue, true);
              clearInput();
            }}
          />
        </EditFormContainer>
      );
    }

    return (
      <EditFormContainer>
        <div className="title">Add new shipping address</div>
        <AddressForm
          disabled={loading}
          value={formValue}
          onChange={setFormValue}
        />
        <ButtonContainer
          sx={{
            marginTop: "7px",
            width: "100%",
            [`@media screen and (max-width: ${TABLET_SCREEN_SIZE}px)`]: {
              marginTop: "20px",
            },
            alignSelf: "flex-end",
            justifyContent: "space-between",
          }}
        >
          <ClearButton
            sx={{
              width: "unset",
              [`@media screen and (max-width: ${TABLET_SCREEN_SIZE}px)`]: {
                width: "100%",
              },
            }}
            onClick={() => {
              clearInput();
              setAddNewOpen(false);
            }}
            disabled={disableCancel || loading}
          >
            Cancel
          </ClearButton>
          <SolidButton
            sx={{
              width: "unset",
              [`@media screen and (max-width: ${TABLET_SCREEN_SIZE}px)`]: {
                width: "100%",
              },
            }}
            disabled={
              !canSubmit ||
              loading ||
              haveAlreadyValidatedInput ||
              validationLoading
            }
            onClick={() => {
              handleValidation();
            }}
          >
            {validationLoading ? "Saving" : "Save"}
          </SolidButton>
        </ButtonContainer>
      </EditFormContainer>
    );
  };

  const sortedAddresses = addresses?.sort((a, b) => {
    if (isAddressEqual(a, selectedAddress)) return -1;
    if (isAddressEqual(b, selectedAddress)) return 1;
    return 0;
  });

  const topFourAddresses = sortedAddresses?.slice(0, 4);

  const remainingAddresses = sortedAddresses?.slice(4);

  return (
    <Container>
      <div className="existing">
        <div className="addresses_container">
          {[...(topFourAddresses ?? [])]
            .sort((a, b) => {
              // Show selected address first
              if (selectedAddressId !== null) {
                if (isAddressEqual(a, selectedAddress)) return -1;
                if (isAddressEqual(b, selectedAddress)) return 1;
              }
              return 0;
            })
            .map((address, i) => {
              const addressDisplay = getFullAddressWithNames(address);
              const selected = selectedAddressId === address.id;

              return (
                <button
                  className="info_row"
                  key={address.id}
                  onClick={() => {
                    setSelectedAddressId(address.id);
                    setAddNewOpen(false);
                    handleSelectShippingAddress?.(address);

                    // Track selecting an existing address
                    if (checkoutIntentId) {
                      trackAction(AnalyticsTrackingEvent.CHECKOUT_STEP_VIEWED, {
                        checkoutIntentId: checkoutIntentId,
                        stepName: "shipping",
                        viewType: "selectShippingAddress",
                        checkoutItems: items || [],
                        checkoutPhase: isPostSubmission
                          ? "post_submission"
                          : "pre_submission",
                        ...(orderId && { orderId }),
                        addressId: address.id,
                      });
                    }
                  }}
                  disabled={loading}
                  style={{
                    fontWeight: selected ? 600 : 500,
                    border: "none",
                    background: "transparent",
                    textAlign: "left",
                    padding: 0,
                    width: "100%",
                    display: "flex",
                    alignItems: "flex-start",
                  }}
                  aria-label={`Select shipping address: ${addressDisplay}`}
                >
                  <StoreRadio
                    key={address.id}
                    sx={{
                      marginRight: "12px",
                      padding: 0,
                      opacity: loading ? 0.5 : 1,
                      [`@media screen and (max-width: ${TABLET_SCREEN_SIZE}px)`]:
                        {
                          marginRight: "10px",
                        },
                    }}
                    disabled={loading}
                    checked={selected}
                    disableRipple
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedAddressId(address.id);
                      setAddNewOpen(false);
                      handleSelectShippingAddress?.(address);

                      // Track selecting an existing address
                      if (checkoutIntentId) {
                        trackAction(
                          AnalyticsTrackingEvent.CHECKOUT_STEP_VIEWED,
                          {
                            checkoutIntentId: checkoutIntentId,
                            stepName: "shipping",
                            viewType: "selectShippingAddress",
                            checkoutItems: items || [],
                            checkoutPhase: isPostSubmission
                              ? "post_submission"
                              : "pre_submission",
                            ...(orderId && { orderId }),
                            addressId: address.id,
                          }
                        );
                      }
                    }}
                    inputProps={{ "aria-hidden": true }}
                  />
                  {addressDisplay}
                </button>
              );
            })}
          {viewMoreAddresses && (
            <>
              {" "}
              {remainingAddresses?.map((address) => {
                const addressDisplay = getFullAddressWithNames(address);
                const selected = selectedAddressId === address.id;

                return (
                  <button
                    className="info_row"
                    key={address.id}
                    onClick={() => {
                      setSelectedAddressId(address.id);
                      setAddNewOpen(false);
                      handleSelectShippingAddress?.(address);

                      // Track selecting an existing address
                      if (checkoutIntentId) {
                        trackAction(
                          AnalyticsTrackingEvent.CHECKOUT_STEP_VIEWED,
                          {
                            checkoutIntentId: checkoutIntentId,
                            stepName: "shipping",
                            viewType: "selectShippingAddress",
                            checkoutItems: items || [],
                            checkoutPhase: isPostSubmission
                              ? "post_submission"
                              : "pre_submission",
                            ...(orderId && { orderId }),
                            addressId: address.id,
                          }
                        );
                      }
                    }}
                    disabled={loading}
                    style={{
                      fontWeight: selected ? 600 : 500,
                      border: "none",
                      background: "transparent",
                      textAlign: "left",
                      padding: 0,
                      width: "100%",
                      display: "flex",
                      alignItems: "flex-start",
                    }}
                    aria-label={`Select shipping address: ${addressDisplay}`}
                  >
                    <StoreRadio
                      key={address.id}
                      sx={{
                        marginRight: "12px",
                        padding: 0,
                        opacity: loading ? 0.5 : 1,
                        [`@media screen and (max-width: ${TABLET_SCREEN_SIZE}px)`]:
                          {
                            marginRight: "10px",
                          },
                      }}
                      disabled={loading}
                      checked={selected}
                      disableRipple
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedAddressId(address.id);
                        setAddNewOpen(false);
                        handleSelectShippingAddress?.(address);

                        // Track selecting an existing address
                        if (checkoutIntentId) {
                          trackAction(
                            AnalyticsTrackingEvent.CHECKOUT_STEP_VIEWED,
                            {
                              checkoutIntentId: checkoutIntentId,
                              stepName: "shipping",
                              viewType: "selectShippingAddress",
                              checkoutItems: items || [],
                              checkoutPhase: isPostSubmission
                                ? "post_submission"
                                : "pre_submission",
                              ...(orderId && { orderId }),
                              addressId: address.id,
                            }
                          );
                        }
                      }}
                      inputProps={{ "aria-hidden": true }}
                    />
                    {addressDisplay}
                  </button>
                );
              })}
            </>
          )}
        </div>

        <button
          className="info_row add_new_button"
          onClick={() => {
            setSelectedAddressId(null);
            setAddNewOpen(true);

            // Track adding a new address
            if (checkoutIntentId) {
              trackAction(AnalyticsTrackingEvent.CHECKOUT_STEP_VIEWED, {
                checkoutIntentId: checkoutIntentId,
                stepName: "shipping",
                viewType: "addShippingAddress",
                checkoutItems: items || [],
                checkoutPhase: isPostSubmission
                  ? "post_submission"
                  : "pre_submission",
                ...(orderId && { orderId }),
              });
            }
          }}
          style={{
            border: "none",
            background: "transparent",
            textAlign: "left",
            padding: 0,
            width: "100%",
            display: "flex",
            alignItems: "center",
          }}
          aria-label="Add new shipping address"
          disabled={loading}
        >
          <AddCircleOutlineIcon className="add_new_icon" />
          Add new shipping address
        </button>
        {remainingAddresses && remainingAddresses.length > 0 && (
          <div
            className="view_more_addresses"
            onClick={() => setViewMoreAddresses(!viewMoreAddresses)}
          >
            {viewMoreAddresses ? "View less" : "View more"}
          </div>
        )}
      </div>

      <ResponsiveDialog open={addNewOpen} onClose={() => setAddNewOpen(false)}>
        {getBottomDrawerContent()}
      </ResponsiveDialog>
    </Container>
  );
};

export default CheckoutEditShippingForm;
